import {rtkApi} from '../../apiSlice';
import {rtkActionHandler} from '../../rtkHandler';
import {
    addNewSchedule,
    admin_printChallanForm,
    admin_printSlip, admin_processPendingPayment,
    admin_saveRegistration,
    admin_updateRegistrationImage,
    admin_updateRegistrationStatus,
    createNewCycle,
    deleteCycle,
    editCycleSchedule,
    generateRegistrationsReport,
    getCenters,
    getClassesForCycles,
    getCycleDetails,
    getCycles,
    getRegistrationDetails,
    getRegistrations,
    getSchedules,
    updateCycleDetails,
    updateCycleStatus,
    updateSchedule,
    updateScheduleStatus
} from "@/redux/apis/admin/cycles";
import {UPDATE_STATUS} from "@/redux/rtk/admin/classes/types";
import {CREATE_CYCLE_FIELDS, CYCLES_FILTER, EDIT_CYCLE_SCHEDULE} from "@/app/admin/cycles/page";
import {CREATE_EDIT_SCHEDULE, SCHEDULE_FILTER} from "@/app/admin/cycles/schedules/[cycleId]/page";
import {STUDENT_REGISTRATION_FILTERS} from "@/app/admin/cycles/studentRegistrations/page";
import {
    ImageUploadRequest,
    ImageUploadResponse,
    TEMPLATE_PROPS,
    UPDATE_USER_REGISTRATION
} from "@/app/students/registrations/[cycleId]/page";
import {REGISTRATION_STATUS} from "@/app/admin/cycles/studentRegistrations/[registrationId]/changeStatusDialog";

const apiWithPortalAdministrationTag = rtkApi.enhanceEndpoints?.({ addTagTypes: ["StudentCycleData"] });

export const cyclesApi = apiWithPortalAdministrationTag.injectEndpoints({
    endpoints: (builder) => ({
        getClassesForCycles: builder.query<any, null>({
            queryFn: async () => {
                return rtkActionHandler(getClassesForCycles());
            },
        }),
        createNewCycle: builder.mutation<any, CREATE_CYCLE_FIELDS>({
            queryFn: async (data: CREATE_CYCLE_FIELDS) => {
                return rtkActionHandler(createNewCycle(data));
            },
        }),
        getCycles: builder.mutation<any, CYCLES_FILTER>({
            queryFn: async (data: CYCLES_FILTER) => {
                return rtkActionHandler(getCycles(data));
            },
        }),
        updateCycleStatus: builder.mutation<any, UPDATE_STATUS>({
            queryFn: async (data: UPDATE_STATUS) => {
                return rtkActionHandler(updateCycleStatus(data));
            },
        }),
        deleteCycle: builder.mutation<any, number>({
            queryFn: async (uid: number) => {
                return rtkActionHandler(deleteCycle({uid}));
            },
        }),
        getCycleDetails: builder.query<any, number>({
            queryFn: async (uid: number) => {
                return rtkActionHandler(getCycleDetails({uid}));
            },
        }),
        updateCycleDetails: builder.mutation<any, CREATE_CYCLE_FIELDS>({
            queryFn: async (data: CREATE_CYCLE_FIELDS) => {
                return rtkActionHandler(updateCycleDetails(data));
            },
        }),
        getCenters: builder.query<any, number>({
            queryFn: async (uid: number) => {
                return rtkActionHandler(getCenters(uid));
            },
        }),
        getSchedules: builder.mutation<any, SCHEDULE_FILTER>({
            queryFn: async (data: SCHEDULE_FILTER) => {
                return rtkActionHandler(getSchedules(data));
            },
        }),
        addNewSchedule: builder.mutation<any, CREATE_EDIT_SCHEDULE>({
            queryFn: async (data: CREATE_EDIT_SCHEDULE) => {
                return rtkActionHandler(addNewSchedule(data));
            },
        }),
        updateSchedule: builder.mutation<any, CREATE_EDIT_SCHEDULE>({
            queryFn: async (data: CREATE_EDIT_SCHEDULE) => {
                return rtkActionHandler(updateSchedule(data));
            },
        }),
        updateScheduleStatus: builder.mutation<any, UPDATE_STATUS>({
            queryFn: async (data: UPDATE_STATUS) => {
                return rtkActionHandler(updateScheduleStatus(data));
            },
        }),
        getRegistrations: builder.mutation<any, STUDENT_REGISTRATION_FILTERS>({
            queryFn: async (data: STUDENT_REGISTRATION_FILTERS) => {
                return rtkActionHandler(getRegistrations(data));
            },
        }),
        getRegistrationDetails: builder.query<any, number>({
            queryFn: async (uid: number) => {
                return rtkActionHandler(getRegistrationDetails(uid));
            },
            providesTags: ['StudentCycleData'],
        }),
        adminPrintChallanForm: builder.mutation<any, TEMPLATE_PROPS>({
            queryFn: async (data: TEMPLATE_PROPS) => {
                return rtkActionHandler(admin_printChallanForm(data));
            }
        }),
        adminPrintSlip: builder.mutation<any, TEMPLATE_PROPS>({
            queryFn: async (data: TEMPLATE_PROPS) => {
                return rtkActionHandler(admin_printSlip(data));
            }
        }),
        adminUpdateRegistrationImage: builder.mutation<ImageUploadResponse, ImageUploadRequest>({
            // @ts-ignore
            queryFn: async (data: ImageUploadRequest) => {
                return rtkActionHandler(admin_updateRegistrationImage(data), {snackbar: true});
            },
        }),
        adminSaveRegistration: builder.mutation<any, UPDATE_USER_REGISTRATION>({
            queryFn: async (data: UPDATE_USER_REGISTRATION) => {
                return rtkActionHandler(admin_saveRegistration(data));
            },
            invalidatesTags: ['StudentCycleData'],
        }),
        adminUpdateRegistrationStatus: builder.mutation<any, REGISTRATION_STATUS>({
            queryFn: async (data: REGISTRATION_STATUS) => {
                return rtkActionHandler(admin_updateRegistrationStatus(data));
            },
            invalidatesTags: ['StudentCycleData'],
        }),
        adminProcessPendingPayment: builder.mutation<any, REGISTRATION_STATUS>({
            queryFn: async (data: REGISTRATION_STATUS) => {
                return rtkActionHandler(admin_processPendingPayment(data));
            },
            invalidatesTags: ['StudentCycleData'],
        }),
        editCycleSchedule: builder.mutation<any, EDIT_CYCLE_SCHEDULE>({
            queryFn: async (data: EDIT_CYCLE_SCHEDULE) => {
                return rtkActionHandler(editCycleSchedule(data));
            },
            invalidatesTags: ['StudentCycleData'],
        }),
        generateRegistrationsReport: builder.mutation<any, STUDENT_REGISTRATION_FILTERS>({
            queryFn: async (data: STUDENT_REGISTRATION_FILTERS) => {
                return rtkActionHandler(generateRegistrationsReport(data));
            },
        }),
    })
})

export const {
    useGetClassesForCyclesQuery,
    useCreateNewCycleMutation,
    useGetCyclesMutation,
    useUpdateCycleStatusMutation,
    useDeleteCycleMutation,
    useGetCycleDetailsQuery,
    useUpdateCycleDetailsMutation,
    useGetCentersQuery,
    useGetSchedulesMutation,
    useAddNewScheduleMutation,
    useUpdateScheduleMutation,
    useUpdateScheduleStatusMutation,
    useGetRegistrationsMutation,
    useGetRegistrationDetailsQuery,
    useAdminPrintChallanFormMutation,
    useAdminPrintSlipMutation,
    useAdminUpdateRegistrationImageMutation,
    useAdminSaveRegistrationMutation,
    useAdminUpdateRegistrationStatusMutation,
    useEditCycleScheduleMutation,
    useGenerateRegistrationsReportMutation,
    useAdminProcessPendingPaymentMutation
} = cyclesApi;
